import { useCurrency } from "@/contexts/CurrencyContext";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { savedCardService } from "@/services/accountServices/savedCardService";
import { paymentService } from "@/services/paymentService";
import { useCallback, useEffect, useState } from "react";

function useSavedCards(extraParam = false) {
  const { currency } = useCurrency();
  const [cardProgram, setCardProgram] = useState(null);
  const [cardList, setCardList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { errorModal } = useModalHelpers();
  const t = useTranslationDetector();

  const getSavedCards = useCallback(async () => {
    setIsLoading(true);

    try {
      const payload = extraParam ? { ForPointConversion: true } : {};
      const response = await savedCardService.getSavedCards(payload);

      setCardList(response.Cards || []);
    } catch (error) {
      setCardList([]);
    } finally {
      setIsLoading(false);
    }
  }, [extraParam]);

  useEffect(() => {
    getSavedCards();
  }, [getSavedCards]);

  const deleteSavedCard = useCallback(
    async (payload) => {
      try {
        const response = await savedCardService.deleteSavedCard(payload);

        await getSavedCards();
        return response;
      } catch (error) {
        errorModal({
          message: t(
            `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
          ),
        });
        throw error;
      }
    },
    [getSavedCards],
  );

  const updateSavedCard = useCallback(
    async (payload) => {
      try {
        const response = await savedCardService.updateSavedCard(payload);

        await getSavedCards();
        return response;
      } catch (error) {
        errorModal({
          message: t(
            `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
          ),
        });
        throw error;
      }
    },
    [getSavedCards],
  );

  const sendSaveCardOTP = useCallback(async (payload) => {
    try {
      const response = await savedCardService.sendSaveCardOTP(payload);
      return response;
    } catch (error) {
      errorModal({
        message: t(
          `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
        ),
      });
      throw error;
    }
  }, []);

  const saveCard = useCallback(
    async (payload) => {
      try {
        const response = await savedCardService.saveCard(payload);

        await getSavedCards();
        return response;
      } catch (error) {
        errorModal({
          message: t(
            `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
          ),
        });
        throw error;
      }
    },
    [getSavedCards],
  );

  const getBinDetails = async (binNumber) => {
    if (binNumber.length < 6) {
      setCardProgram(null);
      return;
    }

    try {
      const response = await paymentService.getBinDetails({
        BinNumber: binNumber,
        Currency: currency.toUpperCase(),
      });

      if (response.Data) {
        setCardProgram(response.Data);
      }
    } catch (error) {
    } finally {
    }
  };

  return {
    cardList,
    isLoading,
    deleteSavedCard,
    updateSavedCard,
    getBinDetails,
    cardProgram,
    sendSaveCardOTP,
    saveCard,
  };
}

export default useSavedCards;
